import { FormEvent, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Layout from '../components/layout/Layout';
import Badge from '../components/common/Badge';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Modal from '../components/common/Modal';
import PageHeader, { EmptyState, KpiTile, SectionCard, Spinner } from '../components/common/PageHeader';
import ResponsiveTable from '../components/common/ResponsiveTable';
import { Checkbox, NativeSelect } from '../components/common/FormField';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { queryKeys } from '../lib/queryClient';
import { api, errorMessage } from '../services/api';
import { BillingCycle, BillingSubscription, Client, CompanyProfile } from '../types';
import { fmtCurrency, fmtDate, fmtNum, todayISO, toNum } from '../utils/format';
import { compact, focusFirstError, hasErrors, serverFieldErrors, v } from '../utils/validation';

const CYCLES: { value: BillingCycle; label: string; per: string }[] = [
  { value: 'monthly', label: 'Monthly', per: 'month' },
  { value: 'quarterly', label: 'Quarterly', per: 'quarter' },
  { value: 'half_yearly', label: 'Half-yearly', per: 'half-year' },
  { value: 'yearly', label: 'Yearly', per: 'year' },
];
const cyclePer = (c: BillingCycle) => CYCLES.find((x) => x.value === c)?.per ?? c;
const cycleLabel = (c: BillingCycle) => CYCLES.find((x) => x.value === c)?.label ?? c;

interface FormState {
  serviceName: string;
  description: string;
  hsnSac: string;
  billingCycle: BillingCycle;
  quantity: string;
  unit: string;
  rate: string;
  prorate: boolean;
  effectiveFrom: string;
}

interface Overview {
  totalMrr: number;
  clients: { clientId: number; companyName: string; subscriptions: number; mrr: number }[];
}

export default function BillingConfigPage() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('billing-config:manage');
  const [searchParams, setSearchParams] = useSearchParams();

  const initialClient = Number(searchParams.get('clientId')) || null;
  const [selectedClientId, setSelectedClientId] = useState<number | null>(initialClient);
  // null = closed; 'new' = adding; a subscription = revising its rate
  const [editing, setEditing] = useState<BillingSubscription | 'new' | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [stopping, setStopping] = useState<BillingSubscription | null>(null);
  const [stopDate, setStopDate] = useState(todayISO());
  const [stopError, setStopError] = useState('');

  const formRef = useRef<HTMLFormElement | null>(null);
  useEffect(() => {
    if (editing) formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [editing]);

  const { data: clients = [] } = useQuery({
    queryKey: queryKeys.clients.all(),
    queryFn: () => api.get<Client[]>('/clients'),
  });
  const { data: company } = useQuery({
    queryKey: queryKeys.company(),
    queryFn: () => api.get<CompanyProfile | null>('/company-profile'),
  });
  const { data: overview } = useQuery({
    queryKey: queryKeys.billingConfigs.overview(),
    queryFn: () => api.get<Overview>('/billing-configs/overview'),
  });
  const { data: active = [], isLoading } = useQuery({
    queryKey: queryKeys.billingConfigs.active(selectedClientId!),
    queryFn: () => api.get<BillingSubscription[]>(`/billing-configs/client/${selectedClientId}/active`),
    enabled: selectedClientId != null,
  });
  const { data: history = [] } = useQuery({
    queryKey: queryKeys.billingConfigs.history(selectedClientId!),
    queryFn: () => api.get<BillingSubscription[]>(`/billing-configs/client/${selectedClientId}/history`),
    enabled: selectedClientId != null,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKeys.billingConfigs.all() });

  const saveMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      editing && editing !== 'new'
        ? api.post(`/billing-configs/${editing.id}/revise`, payload)
        : api.post('/billing-configs', { ...payload, clientId: selectedClientId }),
    onSuccess: invalidate,
  });
  const stopMutation = useMutation({
    mutationFn: () => api.post(`/billing-configs/${stopping!.id}/stop`, { effectiveTo: stopDate }),
    onSuccess: invalidate,
  });

  function selectClient(id: number | null) {
    setSelectedClientId(id);
    setEditing(null);
    setSearchParams(id ? { clientId: String(id) } : {}, { replace: true });
  }

  function openEditor(sub?: BillingSubscription) {
    setErrors({});
    setForm(
      sub
        ? {
            serviceName: sub.serviceName,
            description: sub.description ?? '',
            hsnSac: sub.hsnSac,
            billingCycle: sub.billingCycle,
            quantity: String(sub.quantity),
            unit: sub.unit,
            rate: String(sub.rate),
            prorate: sub.prorate,
            effectiveFrom: '',
          }
        : {
            serviceName: '',
            description: '',
            hsnSac: company?.defaultHsnSac ?? '998314',
            billingCycle: 'monthly',
            quantity: '1',
            unit: 'Nos',
            rate: '',
            prorate: true,
            effectiveFrom: `${todayISO().slice(0, 7)}-01`,
          },
    );
    setEditing(sub ?? 'new');
  }

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((p) => (p ? { ...p, [key]: value } : p));
    setErrors((p) => (p[key] ? { ...p, [key]: undefined } : p));
  }

  function validate(f: FormState): boolean {
    const name = f.serviceName.trim().toLowerCase();
    const duplicate =
      editing === 'new' && active.some((s) => s.serviceName.toLowerCase() === name)
        ? 'This service is already active for the client — use Revise Rate instead'
        : undefined;
    let effectiveFrom = v.date(f.effectiveFrom, 'Effective From');
    if (!effectiveFrom && editing && editing !== 'new' && f.effectiveFrom <= editing.effectiveFrom) {
      effectiveFrom = `Must be after ${fmtDate(editing.effectiveFrom)} (current rate start)`;
    }
    const e = compact({
      serviceName: v.businessName(f.serviceName, 'Service name', true, 150, 3) ?? duplicate,
      description: v.text(f.description, 'Description', { max: 255 }),
      hsnSac: v.hsn(f.hsnSac),
      quantity: v.quantity(f.quantity),
      unit: v.unit(f.unit),
      rate: v.money(f.rate, 'Rate'),
      effectiveFrom,
    });
    setErrors(e);
    if (hasErrors(e)) focusFirstError(formRef.current);
    return !hasErrors(e);
  }

  async function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    if (!form || !selectedClientId || !validate(form)) return;
    try {
      await saveMutation.mutateAsync({
        serviceName: form.serviceName.trim(),
        description: form.description.trim() || undefined,
        hsnSac: form.hsnSac,
        billingCycle: form.billingCycle,
        quantity: toNum(form.quantity),
        unit: form.unit.trim(),
        rate: toNum(form.rate),
        prorate: form.prorate,
        effectiveFrom: form.effectiveFrom,
      });
      showToast(editing === 'new' ? 'Subscription added' : 'Rate revised', 'success');
      setEditing(null);
    } catch (err) {
      setErrors(serverFieldErrors(err, ['serviceName', 'description', 'hsnSac', 'quantity', 'unit', 'rate', 'effectiveFrom'] as const));
      showToast(errorMessage(err, 'Failed to save subscription'), 'error');
    }
  }

  async function handleStop() {
    const dateError =
      v.date(stopDate, 'Last billed day', { min: stopping?.effectiveFrom, minLabel: `the start date (${fmtDate(stopping?.effectiveFrom)})` });
    setStopError(dateError ?? '');
    if (dateError) return;
    try {
      await stopMutation.mutateAsync();
      showToast('Subscription stopped', 'success');
      setStopping(null);
    } catch (err) {
      showToast(errorMessage(err, 'Failed to stop subscription'), 'error');
    }
  }

  const pastVersions = history.filter((h) => !h.isActive);
  const clientMrr = overview?.clients.find((c) => c.clientId === selectedClientId)?.mrr ?? 0;
  const previewAmount = form ? (toNum(form.quantity) ?? 0) * (toNum(form.rate) ?? 0) : 0;

  const historyColumns = [
    { key: 'serviceName', label: 'Service', render: (_: any, r: BillingSubscription) => <span className="text-sm font-medium text-ink-800">{r.serviceName}</span> },
    { key: 'effectiveFrom', label: 'Effective From', render: (_: any, r: BillingSubscription) => fmtDate(r.effectiveFrom) },
    { key: 'effectiveTo', label: 'Effective To', render: (_: any, r: BillingSubscription) => fmtDate(r.effectiveTo) },
    { key: 'quantity', label: 'Qty', render: (_: any, r: BillingSubscription) => `${fmtNum(r.quantity)} ${r.unit}` },
    { key: 'rate', label: 'Rate', render: (_: any, r: BillingSubscription) => `${fmtCurrency(r.rate)} / ${cyclePer(r.billingCycle)}` },
    { key: 'amountPerCycle', label: 'Amount', render: (_: any, r: BillingSubscription) => fmtCurrency(r.amountPerCycle) },
    { key: 'status', label: 'Status', render: () => <Badge variant="secondary" size="sm">Expired</Badge> },
  ];

  return (
    <Layout>
      <div className="max-w-5xl mx-auto">
        <PageHeader
          title="Billing Configuration"
          subtitle="Set what each client is billed every cycle — the monthly invoice run reads these subscriptions."
        />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4 lg:mb-6">
          <KpiTile label="Monthly Recurring Revenue" value={fmtCurrency(overview?.totalMrr ?? 0, 0)} hint="All active subscriptions, excl. GST" icon="fa-arrows-rotate" tone="brand" />
          <KpiTile label="Subscribed Clients" value={overview?.clients.length ?? 0} icon="fa-building-user" tone="accent" />
          <KpiTile label="This Client / Month" value={selectedClientId ? fmtCurrency(clientMrr, 0) : '—'} icon="fa-file-invoice-dollar" tone="green" />
        </div>

        <div className="bg-white border border-ink-200 rounded-xl p-4 mb-4 lg:mb-6">
          <NativeSelect
            label="Select Client"
            value={selectedClientId ?? ''}
            onChange={(val) => selectClient(val ? Number(val) : null)}
            placeholder="Select a client…"
            options={clients.map((c) => ({ value: c.id, label: c.isActive ? c.companyName : `${c.companyName} (inactive)` }))}
          />
        </div>

        {selectedClientId == null && (
          <EmptyState icon="fa-sliders" message="Select a client above to view or configure its subscriptions." />
        )}

        {selectedClientId != null && isLoading && <Spinner />}

        {selectedClientId != null && !isLoading && (
          <>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-bold uppercase tracking-wider text-ink-600">
                Active Subscriptions ({active.length})
              </h2>
              {canManage && (
                <Button variant="primary" size="sm" icon="fa-plus" onClick={() => openEditor()}>
                  Add Subscription
                </Button>
              )}
            </div>

            {active.length === 0 && !editing && (
              <EmptyState icon="fa-file-circle-plus" message="No active subscriptions for this client yet." />
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {active.map((sub) => (
                <div key={sub.id} className="bg-white border border-ink-200 rounded-lg overflow-hidden">
                  <div className="bg-ink-50 border-b border-ink-200 px-4 py-2.5 flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold text-ink-600 uppercase tracking-wider truncate">{sub.serviceName}</span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800 shrink-0">
                      {cycleLabel(sub.billingCycle)}
                    </span>
                  </div>
                  <div className="p-4">
                    {sub.description && <p className="text-xs text-ink-500 mb-2">{sub.description}</p>}
                    <div className="space-y-1.5 mb-3">
                      <Row label="Quantity" value={`${fmtNum(sub.quantity)} ${sub.unit}`} />
                      <Row label={`Rate / ${sub.unit} / ${cyclePer(sub.billingCycle)}`} value={fmtCurrency(sub.rate)} />
                      <Row label={`Amount / ${cyclePer(sub.billingCycle)}`} value={fmtCurrency(sub.amountPerCycle)} strong />
                      <Row label="HSN/SAC" value={sub.hsnSac} />
                      <Row label="Active from" value={fmtDate(sub.effectiveFrom)} />
                      <Row label="Proration" value={sub.billingCycle === 'monthly' ? (sub.prorate ? 'On' : 'Off') : 'n/a'} />
                    </div>
                    {canManage && (
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => openEditor(sub)}
                          className="flex-1 px-3 py-1.5 text-xs font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 transition-colors"
                        >
                          Revise Rate
                        </button>
                        <button
                          type="button"
                          onClick={() => { setStopping(sub); setStopDate(todayISO()); setStopError(''); }}
                          className="px-3 py-1.5 text-xs font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                        >
                          Stop
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {editing && form && (
              <form ref={formRef} onSubmit={handleSubmit} className="space-y-4 mb-6" noValidate>
                <div className="bg-brand-50 border border-brand-200 rounded-lg px-4 py-2.5 flex items-center justify-between">
                  <span className="text-sm font-medium text-brand-800">
                    {editing === 'new' ? 'Adding subscription' : `Revising rate — ${editing.serviceName}`}
                  </span>
                  <button type="button" onClick={() => setEditing(null)} className="text-xs text-brand-600 hover:text-brand-800 underline">
                    Cancel
                  </button>
                </div>

                <SectionCard title="Service">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input label="Service Name" required value={form.serviceName} maxLength={150}
                      placeholder="e.g. Fleecams Platform Subscription"
                      onChange={(e) => setField('serviceName', e.target.value)} error={errors.serviceName}
                      helperText="Printed in bold on the invoice line" />
                    <Input label="Description" value={form.description} maxLength={255}
                      placeholder="e.g. ADAS + DMS live monitoring"
                      onChange={(e) => setField('description', e.target.value)} error={errors.description} />
                    <Input label="HSN / SAC" required value={form.hsnSac} maxLength={8}
                      onChange={(e) => setField('hsnSac', e.target.value.replace(/\D/g, ''))} error={errors.hsnSac}
                      helperText="4-8 digits" />
                    <NativeSelect label="Billing Cycle" value={form.billingCycle}
                      onChange={(val) => setField('billingCycle', val as BillingCycle)}
                      options={CYCLES.map((c) => ({ value: c.value, label: c.label }))} />
                  </div>
                </SectionCard>

                <SectionCard title="Pricing">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Input label="Quantity" required type="number" decimals={3} value={form.quantity}
                      onChange={(e) => setField('quantity', e.target.value)} error={errors.quantity} />
                    <Input label="Unit" required value={form.unit} maxLength={30} placeholder="Vehicle / User / Licence"
                      onChange={(e) => setField('unit', e.target.value)} error={errors.unit} />
                    <Input label={`Rate per unit / ${cyclePer(form.billingCycle)} (₹)`} required type="number" value={form.rate}
                      onChange={(e) => setField('rate', e.target.value)} error={errors.rate} helperText="Excluding GST" />
                  </div>
                  <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                    {form.billingCycle === 'monthly' ? (
                      <Checkbox checked={form.prorate} onChange={(val) => setField('prorate', val)}
                        label="Prorate partial months"
                        hint="Charge only for the days live when the service starts or stops mid-month" />
                    ) : (
                      <p className="text-xs text-ink-500">
                        Billed in advance, in full, in the month each {cyclePer(form.billingCycle)} starts.
                      </p>
                    )}
                    <p className="text-sm text-ink-600">
                      Amount / {cyclePer(form.billingCycle)}:{' '}
                      <span className="font-bold text-ink-900">{fmtCurrency(previewAmount)}</span>
                    </p>
                  </div>
                </SectionCard>

                <div className="bg-white border border-ink-200 rounded-lg p-4">
                  <div className="flex flex-wrap items-end gap-4">
                    <div className="w-56">
                      <Input label="Effective From" type="date" required value={form.effectiveFrom}
                        onChange={(e) => setField('effectiveFrom', e.target.value)} error={errors.effectiveFrom} />
                    </div>
                    <div className="flex gap-3 mb-1">
                      <Button type="submit" variant="primary" disabled={saveMutation.isPending}>
                        {saveMutation.isPending ? 'Saving…' : 'Save'}
                      </Button>
                      <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                    </div>
                  </div>
                  {editing !== 'new' && (
                    <p className="text-xs text-amber-600 mt-2">
                      Saving creates a new rate from the date above and expires the current one the day before.
                      Invoices already generated are not changed.
                    </p>
                  )}
                </div>
              </form>
            )}

            {pastVersions.length > 0 && (
              <SectionCard title={`Rate History (${pastVersions.length})`} bodyClassName="px-1 py-3 lg:p-0">
                <ResponsiveTable columns={historyColumns} data={pastVersions} height="auto" emptyMessage="No history" />
              </SectionCard>
            )}
          </>
        )}

        <Modal
          isOpen={!!stopping}
          onClose={() => setStopping(null)}
          title="Stop Subscription"
          size="sm"
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setStopping(null)}>Cancel</Button>
              <Button variant="danger" onClick={handleStop} disabled={stopMutation.isPending || !stopDate}>
                {stopMutation.isPending ? 'Stopping…' : 'Stop Subscription'}
              </Button>
            </div>
          }
        >
          <p className="text-sm text-ink-600 mb-4">
            <span className="font-semibold text-ink-900">{stopping?.serviceName}</span> will not be billed after this date.
          </p>
          <Input label="Last billed day" type="date" required value={stopDate} min={stopping?.effectiveFrom} error={stopError}
            onChange={(e) => { setStopDate(e.target.value); setStopError(''); }} />
        </Modal>
      </div>
    </Layout>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-center justify-between text-xs gap-3">
      <span className="text-ink-500">{label}</span>
      <span className={strong ? 'font-bold text-ink-900 text-sm' : 'font-medium text-ink-800'}>{value}</span>
    </div>
  );
}
